// ---------------------------------------------------------------------------
// Renderer: Chart
//
// Bar, line, heatmap, progress, and pie charts.  Handles y-axis range
// computation, nice tick generation, gridlines, value labels, x-axis label
// stride, legend, conditional coloring, and per-kind rendering.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { themeColor, defaultTheme } from "../renderers/plainObjectRenderer";
import { renderPieChart } from "./pie.js";
import { evaluateConditionalColor } from "../shared/condition-evaluator.js";
import { formatValue } from "../shared/formatting.js";
import { tagEvidence, addDebugOverlay } from "./helpers.js";

// ---------------------------------------------------------------------------
// blendHex — blend two #rrggbb colors. t=0→a, t=1→b. Local utility.
// ---------------------------------------------------------------------------
function blendHex(a, b, t) {
  const parse = h => {
    const m = String(h).replace("#", "");
    return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
  };
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  const mix = (x, y) => Math.round(x + (y - x) * Math.max(0, Math.min(1, t)));
  const hx = n => n.toString(16).padStart(2, "0");
  return `#${hx(mix(ar, br))}${hx(mix(ag, bg))}${hx(mix(ab, bb))}`;
}

// Format an above-bar / above-point value label.
//
// Shared by the bar and line render paths so that swapping a chart's `kind`
// never changes how a value is labeled. Precedence (matches the y-axis so the
// label and gridlines always agree):
//   1. y_axis.value_divisor → divide the raw value and round to value_decimals.
//      Data is often stored in a scaled unit (e.g. millions) while the axis
//      unit is "$bn"; without this the label would be re-scaled by formatValue
//      (9000 → "$9.0K") and contradict the axis.
//   2. series.label_format → a named formatValue style ("currency", "num_1dp"…).
//   3. raw value as a string.
function formatPointLabel(val, yAxis, ser, chartData) {
  if (yAxis.value_divisor) {
    const scaled = val / yAxis.value_divisor;
    // The axis decimals are chosen for the LARGEST series (25.3bn → 0dp), which
    // flattens smaller series on the same chart (2.5bn and 3.2bn both → "3").
    // Guarantee every label carries at least 2 significant figures.
    const base = yAxis.value_decimals ?? 1;
    let needed = 0;
    const abs = Math.abs(scaled);
    if (abs > 0) {
      needed = abs >= 1
        ? Math.max(0, 2 - (Math.floor(Math.log10(abs)) + 1))
        : Math.min(4, -Math.floor(Math.log10(abs)) + 1);
    }
    return scaled.toFixed(Math.max(base, needed));
  }
  if (ser.label_format) {
    return formatValue(val, ser.label_format, {
      currency: chartData?.currency,
      valuesUnit: chartData?.values_unit
    });
  }
  return String(val);
}

// Resolve the effective y-axis config. When the crystallized data carries
// currency/scale hints (financial-pin charts), the unit, divisor and decimals
// are derived from the data — currency from the source details, scale from the
// value magnitudes — so one DSL template works for a €650m mid-cap and a $12bn
// issuer alike. A hard-coded "unit": "$bn" in a reusable template is wrong for
// every non-USD supplier, so data wins by default; set `y_axis: { auto: false }`
// to force the literal config. Charts without hints (no financial pin) always
// use the literal config.
const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", GBP: "£", JPY: "¥", CHF: "CHF ", CNY: "¥", INR: "₹" };
function resolveYAxis(block, chartData) {
  const cfg = block.y_axis || {};
  const hasHints = chartData?.values_unit !== undefined && chartData?.currency;
  if (cfg.auto === false || (!cfg.auto && !hasHints)) return cfg;
  const sym = CURRENCY_SYMBOLS[chartData?.currency] ?? "";
  const vals = Object.values(chartData?.series || {})
    .flat()
    .filter(v => typeof v === "number" && Number.isFinite(v));
  const maxAbs = vals.length ? Math.max(...vals.map(Math.abs)) : 0;
  const inMillions = chartData?.values_unit !== "raw";
  if (inMillions) {
    if (maxAbs >= 1000) {
      return { ...cfg, unit: `${sym}bn`, value_divisor: 1000, value_decimals: maxAbs >= 10000 ? 0 : 1 };
    }
    return { ...cfg, unit: `${sym}m`, value_divisor: 1, value_decimals: maxAbs < 10 ? 1 : 0 };
  }
  if (maxAbs >= 1000) {
    return { ...cfg, unit: `${sym}k`, value_divisor: 1000, value_decimals: 0 };
  }
  return { ...cfg, unit: sym, value_divisor: 1, value_decimals: 0 };
}

export function renderChart(block, crystalizedData, options) {
  const { x = 0, y = 0, width = 400, height = 300, imageCallback, theme, fontScale = 1 } = options;

  // Derive a local font scale from chart dimensions so text is always
  // proportionate to chart size. The global fontScale (from slide width / 1280)
  // is the nominal scale for the slide, but the chart computes its own scale
  // from its allocated dimensions. The two are blended: we take the maximum
  // of the inherited fontScale and a ratio-based scale. This means:
  //  - Large charts get proportionally larger text
  //  - Small charts still get readable text (floor at 0.5)
  //  - Phase 3 text-shrinking can't crush chart text below the chart's
  //    natural scale because the ratio-based component is independent.
  // Reference chart size: 400 × 300 → ratio = 1.0
  const _refMinDim = 300;
  const _ratio = Math.min(width, height) / _refMinDim;
  const _ratioScale = Math.max(0.5, Math.min(2.0, _ratio));
  // Progress bars are text + a thin line — scale with fontScale only, not chart dimensions.
  // This keeps measurement and rendering consistent (measureNaturalHeight passes height=99999
  // which would otherwise inflate _ratioScale to 2.0 and make Phase 3 ineffective).
  const s = block.kind === "progress" ? fontScale : Math.max(fontScale, _ratioScale);

  const themeColors = theme || defaultTheme;
  const group = new Konva.Group({ x, y, width, height });
  const chartData = crystalizedData[block.id];

  if (!chartData) {
    const isPreview = options._preview;
    if (isPreview) {
      const isProgress = block.kind === "progress";

      if (isProgress) {
        // Progress bar skeleton: thin horizontal bar with label placeholder.
        const pad = Math.round(16 * s);
        const labelW = Math.round(140 * s);
        const barH = Math.round(block.bar_height ? block.bar_height * s : 6 * s);
        const barY = Math.round((height - barH) / 2);
        const barFullW = width - pad * 2;
        const SKEL = "#e8e8e8";

        group.add(new Konva.Rect({ width, height, fill: "#fafafa" }));

        group.add(new Konva.Rect({
          x: pad, y: barY - Math.round(2 * s),
          width: labelW, height: Math.round(6 * s),
          fill: SKEL, cornerRadius: Math.round(2 * s)
        }));

        const trackX = pad + labelW + Math.round(16 * s);
        const trackW = width - trackX - pad;
        group.add(new Konva.Rect({
          x: trackX, y: barY, width: trackW, height: barH,
          fill: "#f0f0f0", stroke: "#e0e0e0", strokeWidth: Math.round(0.5 * s),
          cornerRadius: Math.round(2 * s)
        }));

        const valW = Math.round(80 * s);
        group.add(new Konva.Rect({
          x: width - pad - valW, y: barY - Math.round(5 * s),
          width: valW, height: Math.round(8 * s),
          fill: SKEL, cornerRadius: Math.round(2 * s)
        }));
      } else {
        // Bar/line chart skeleton: vertical bars with axis.
        const pad = Math.round(30 * s);
        const barAreaW = width - pad * 2;
        const barAreaH = height - pad * 2;
        const barCount = 4;
        const barGap = Math.round(12 * s);
        const barW = Math.max(4, (barAreaW - barGap * (barCount + 1)) / barCount);
        const baseY = pad + barAreaH;
        const barHeights = barAreaH > 0
          ? [0.75, 0.55, 0.90, 0.65].map(r => Math.max(4, Math.round(barAreaH * r)))
          : [30, 20, 35, 25];
        const SKEL = "#e8e8e8";

        group.add(new Konva.Rect({ width, height, fill: "#fafafa", stroke: "#e0e0e0", strokeWidth: 1 }));

        const axisY = pad + barAreaH;
        group.add(new Konva.Line({
          points: [pad, axisY, width - pad, axisY],
          stroke: "#d0d0d0", strokeWidth: Math.round(1 * s), dash: [Math.round(4 * s), Math.round(3 * s)]
        }));

        for (let i = 0; i < barCount; i++) {
          const bx = pad + barGap + i * (barW + barGap);
          const by = baseY - barHeights[i];
          group.add(new Konva.Rect({
            x: bx, y: by, width: barW, height: barHeights[i],
            fill: SKEL, cornerRadius: Math.round(2 * s)
          }));
          const xLabel = new CustomText({
            x: bx - Math.round(4 * s), y: axisY + Math.round(4 * s), width: barW + Math.round(8 * s),
            text: "FYxx", fontSize: Math.round(9 * s), fill: SKEL, align: "center",
            _preview: true, refreshCallback: imageCallback
          });
          group.add(xLabel);
        }
      }
    } else {
      const label = new CustomText({
        x: Math.round(10 * s), y: height / 2 - Math.round(10 * s), width: width - Math.round(20 * s),
        text: "No data", fontSize: Math.round(14 * s), fill: "#999", align: "center",
        refreshCallback: imageCallback
      });
      group.add(new Konva.Rect({ width, height, fill: "#f5f5f5", stroke: "#ddd", strokeWidth: 1 }), label);
    }
    addDebugOverlay(group, block, options);
    return group;
  }

  // ── Heatmap ──
  if (block.kind === "heatmap") {
    const cells = Array.isArray(chartData.cells) ? chartData.cells : [];
    const pad = Math.round(8 * s);
    const labelText = block.label || "";
    let topY = pad;
    if (labelText) {
      const lbl = new CustomText({
        x: pad, y: pad, width: width - pad * 2,
        text: labelText.toUpperCase(), fontSize: Math.round(10 * s), fontStyle: "bold",
        letterSpacing: Math.round(1 * s), fill: "#888", refreshCallback: imageCallback
      });
      group.add(lbl);
      topY = pad + lbl.height() + Math.round(6 * s);
    }
    const cellGap = Math.round(4 * s);
    const cellW = cells.length > 0 ? (width - pad * 2 - cellGap * (cells.length - 1)) / cells.length : 0;
    const belowLabelH = Math.round(14 * s);
    const cellH = Math.round((block.cell_height || 34) * s);
    if (cells.length > 0 && cellW > 2 && cellH > 2) {
      const heatMax = block.heat_color || "#6E1E2B";
      const corner = Math.max(0, Math.min(Math.round(3 * s), cellW / 2, cellH / 2));
      const maxVal = Math.max(1, ...cells.map(c => Number(c.value) || 0));
      let hx = pad;
      for (const c of cells) {
        const v = Number(c.value) || 0;
        const t = v <= 0 ? 0 : 0.25 + 0.75 * (v / maxVal);
        const fill = v <= 0 ? "#EEE9E9" : blendHex("#FFFFFF", heatMax, t);
        group.add(new Konva.Rect({ x: hx, y: topY, width: cellW, height: cellH, fill, cornerRadius: corner }));
        group.add(new CustomText({
          x: hx, y: topY + cellH / 2 - Math.round(8 * s), width: cellW,
          text: String(v), fontSize: Math.round(14 * s), fontStyle: "bold", align: "center",
          fill: t > 0.55 ? "#fff" : "#4a3033", refreshCallback: imageCallback
        }));
        group.add(new CustomText({
          x: hx, y: topY + cellH + Math.round(3 * s), width: cellW,
          text: c.label || "", fontSize: Math.round(8 * s), align: "center",
          fill: "#999", refreshCallback: imageCallback
        }));
        hx += cellW + cellGap;
      }
    }
    group.height(topY + cellH + belowLabelH + pad);
    addDebugOverlay(group, block, options);
    return group;
  }

  const isBarOrLine = block.kind === "bar" || block.kind === "line" || !block.kind;
  if (isBarOrLine && !chartData.series) {
    const label = new CustomText({
      x: Math.round(10 * s), y: height / 2 - Math.round(10 * s), width: width - Math.round(20 * s),
      text: "No chart data", fontSize: Math.round(14 * s), fill: "#999", align: "center",
      refreshCallback: imageCallback
    });
    group.add(new Konva.Rect({ width, height, fill: "#f5f5f5", stroke: "#ddd", strokeWidth: 1 }), label);
    addDebugOverlay(group, block, options);
    return group;
  }

  const isProgress = block.kind === "progress";
  const padding = isProgress
    ? { top: Math.round(2 * s), right: Math.round(4 * s), bottom: Math.round(2 * s), left: Math.round(4 * s) }
    : { top: Math.round(28 * s), right: Math.round(16 * s), bottom: Math.round(28 * s), left: Math.round(44 * s) };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  group.add(new Konva.Rect({ width, height, fill: "#fff" }));

  // ── Y-axis range, ticks, gridlines ──
  let yMin = 0, yMax = 1;
  if (!isProgress && block.kind !== "pie" && block.kind !== "coverage") {
    const allValues = [];
    for (const ser of (block.series || [])) {
      const vals = chartData.series?.[ser.key];
      if (Array.isArray(vals)) allValues.push(...vals.filter(v => v != null));
    }
    if (allValues.length > 0) {
      const dataMax = Math.max(...allValues);
      const dataMin = Math.min(...allValues);
      if (block.kind === "line") {
        const span = dataMax - dataMin;
        const pad = span > 0 ? span * 0.08 : Math.abs(dataMax) * 0.05 || 1;
        yMin = dataMin - pad;
        yMax = dataMax + pad;
      } else {
        yMax = Math.max(0, dataMax);
        yMin = Math.min(0, dataMin);
      }
    }
  }

  function niceTicks(min, max, target = 5) {
    if (max === min) return [min];
    const roughStep = (max - min) / target;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;
    let niceStep;
    if (normalized < 1.5) niceStep = magnitude;
    else if (normalized < 3) niceStep = 2 * magnitude;
    else if (normalized < 7) niceStep = 5 * magnitude;
    else niceStep = 10 * magnitude;
    const niceMin = Math.floor(min / niceStep) * niceStep;
    const niceMax = Math.ceil(max / niceStep) * niceStep;
    const out = [];
    for (let v = niceMin; v <= niceMax + niceStep * 0.001; v += niceStep) {
      out.push(Math.round(v / niceStep) * niceStep);
    }
    return out;
  }

  let yTicks = [];
  if (!isProgress && block.kind !== "pie" && block.kind !== "coverage") {
    const tickTarget = Math.max(3, Math.min(7, Math.round(plotH / Math.round(50 * s))));
    yTicks = niceTicks(yMin, yMax, tickTarget);
    if (yTicks.length > 0) {
      yMin = Math.min(yMin, yTicks[0]);
      yMax = Math.max(yMax, yTicks[yTicks.length - 1]);
    }
  }
  const yRange = yMax - yMin || 1;
  const yToPx = v => padding.top + plotH * (1 - (v - yMin) / yRange);

  if (block.id === 'revenue_chart' && !isProgress && block.kind !== "pie" && block.kind !== "coverage") {
    const _xL = (chartData && chartData.x_labels) || [];
    const _sK = Object.keys(chartData.series || {});
    console.log(`[Chart] revenue_chart yRange=${yRange} yMin=${yMin} yMax=${yMax} yTicks=[${yTicks.join(',')}]
      plotW=${plotW} plotH=${plotH} padding=${JSON.stringify(padding)}
      seriesKeys=[${_sK}] xLabels=[${_xL}]`);
  }

  const effYAxis = resolveYAxis(block, chartData);

  if (!isProgress && block.kind !== "pie" && block.kind !== "coverage") {
    const gridDashed = effYAxis?.gridlines?.dashed !== false;
    const yAxisCfg = effYAxis;
    const divisor = yAxisCfg.value_divisor || 1;
    const decimals = yAxisCfg.value_decimals ?? 0;
    const unit = yAxisCfg.unit || "";
    const unitPrefix = (unit.startsWith("£") || unit.startsWith("$") || unit.startsWith("€")) ? unit[0] : "";
    for (const tick of yTicks) {
      const gy = yToPx(tick);
      group.add(new Konva.Line({
        points: [padding.left, gy, padding.left + plotW, gy],
        stroke: "#e0e0e0", strokeWidth: 0.5,
        dash: gridDashed ? [4, 4] : []
      }));
      group.add(new CustomText({
        x: Math.round(4 * s), y: gy - Math.round(6 * s),
        width: padding.left - Math.round(8 * s),
        text: `${unitPrefix}${(tick / divisor).toFixed(decimals)}`,
        fontSize: Math.round(10 * s), fill: "#666", align: "right",
        refreshCallback: imageCallback
      }));
    }
    group.add(new Konva.Line({
      points: [padding.left, padding.top, padding.left, padding.top + plotH],
      stroke: "#bbb", strokeWidth: 0.6
    }));
    group.add(new Konva.Line({
      points: [padding.left, padding.top + plotH, padding.left + plotW, padding.top + plotH],
      stroke: "#bbb", strokeWidth: 0.6
    }));
  }

  if (effYAxis.unit && !isProgress && effYAxis.unit.length > 1) {
    group.add(new CustomText({
      x: Math.round(6 * s), y: Math.round(8 * s),
      width: padding.left - Math.round(8 * s),
      text: effYAxis.unit, fontSize: Math.round(11 * s),
      fill: "#888", refreshCallback: imageCallback
    }));
  }

  const xLabels = chartData.x_labels || [];
  const seriesList = block.series || [];
  const barGroupWidth = plotW / Math.max(xLabels.length, 1);
  let baselineY = padding.top + plotH;
  let barYScale = 1;
  let barPlotH = plotH;

  if (block.kind === "coverage") {
    const pieColors = block.palette?.colors || ["#2C4A6E", "#4A6E8E", "#6E8EAE", "#8EAECC", "#AECCE8"];
    const allSegments = (chartData.segments || [])
      .map((seg, i) => ({
        label: seg.label, count: seg.count ?? 0, total: seg.total ?? 0,
        color: seg.color ?? pieColors[i % pieColors.length]
      }))
      .sort((a, b) => block.sort === "label" ? a.label.localeCompare(b.label) : 0);

    // Coverage bars: label | track | pct — rows distributed evenly across height
    const pad       = Math.round(10 * s);
    const innerW    = width - pad * 2;
    const innerH    = height - pad * 2;
    const n         = allSegments.length || 1;
    const rowH      = Math.floor(innerH / n);
    const barThk    = Math.min(Math.round(10 * s), Math.round(rowH * 0.4));
    const pctW      = Math.round(34 * s);
    const pctGap    = Math.round(4 * s);
    const labelFrac = 0.44;
    const labelW    = Math.round(innerW * labelFrac);
    const trackW    = innerW - labelW - pctW - pctGap;

    allSegments.forEach((seg, i) => {
      const rowY   = pad + i * rowH;
      const pct    = seg.total > 0 ? seg.count / seg.total : 0;
      const fillW  = Math.max(0, Math.round(trackW * pct));
      const trackX = pad + labelW;
      const barY   = rowY + Math.round((rowH - barThk) / 2);

      group.add(new CustomText({
        x: pad, y: rowY,
        width: labelW,
        text: seg.label,
        fontSize: Math.round(11 * s),
        fill: seg.count > 0 ? "#333" : "#aaa",
        wrap: "none", ellipsis: true,
        refreshCallback: imageCallback
      }));

      group.add(new Konva.Rect({
        x: trackX, y: barY,
        width: trackW, height: barThk,
        fill: "#eee", cornerRadius: Math.round(barThk / 2)
      }));

      if (fillW > 0) {
        group.add(new Konva.Rect({
          x: trackX, y: barY,
          width: fillW, height: barThk,
          fill: seg.color, cornerRadius: Math.round(barThk / 2)
        }));
      }

      group.add(new CustomText({
        x: trackX + trackW + pctGap, y: rowY,
        width: pctW,
        text: seg.total > 0 ? `${Math.round(pct * 100)}%` : "–",
        fontSize: Math.round(11 * s),
        fill: "#666",
        align: "right",
        refreshCallback: imageCallback
      }));
    });
  } else if (block.kind === "pie") {
    const pieColors = block.palette?.colors || ["#2C4A6E", "#4A6E8E", "#6E8EAE", "#8EAECC", "#AECCE8"];
    const allSegments = (chartData.segments || []).map((seg, i) => ({
      label: seg.label, count: seg.count ?? 0,
      color: seg.color ?? pieColors[i % pieColors.length]
    }));

    const dotR        = Math.round(4 * s);
    const legendItemH = Math.round(16 * s);
    const legendGap   = Math.round(3 * s);
    const legendTop   = Math.round(10 * s);

    const dotOverhead = dotR * 2 + Math.round(6 * s);
    const minColW     = dotOverhead + Math.round(72 * s);
    const numCols     = Math.min(3, Math.max(1, Math.floor(plotW / minColW), 1), allSegments.length);
    const numRows     = Math.ceil(allSegments.length / numCols);
    const legendH     = numRows * (legendItemH + legendGap) + legendTop;

    const availH  = Math.max(0, plotH - legendH);
    const pieSize = Math.min(plotW, availH);
    const pieX    = padding.left + (plotW - pieSize) / 2;
    const pieY    = padding.top  + (availH - pieSize) / 2;

    group.add(renderPieChart(allSegments, { size: pieSize, x: pieX, y: pieY, colors: pieColors }));

    const colW         = plotW / numCols;
    const legendStartY = padding.top + availH + legendTop;

    allSegments.forEach((seg, i) => {
      const col    = i % numCols;
      const row    = Math.floor(i / numCols);
      const cellX  = padding.left + col * colW;
      const cellY  = legendStartY + row * (legendItemH + legendGap);
      const isZero = seg.count === 0;

      group.add(new Konva.Circle({
        x: cellX + dotR, y: cellY + legendItemH / 2,
        radius: dotR,
        fill: isZero ? "#ccc" : seg.color
      }));
      group.add(new CustomText({
        x: cellX + dotR * 2 + Math.round(4 * s), y: cellY,
        width: colW - dotR * 2 - Math.round(4 * s),
        text: seg.label,
        fontSize: Math.round(10 * s),
        fill: isZero ? "#aaa" : "#444",
        wrap: "none", ellipsis: true,
        refreshCallback: imageCallback
      }));
    });
  } else if (block.kind === "progress") {
    const pct = typeof chartData.value === "number" ? Math.min(100, Math.max(0, chartData.value)) : 0;
    const barH = Math.round(block.bar_height ? block.bar_height * s : 6 * s);
    const colorRule = block.color_rule || {};
    const dv = chartData.display_value;
    const hasValue = chartData.value != null;
    const hasLabel = dv != null && String(dv).trim() !== "";
    const isNA = !hasValue && !hasLabel;
    const isSetOnly = !hasValue && hasLabel;
    const barColor = isNA ? (colorRule.na || "#888780") : (colorRule.on_track || "#2D8659");
    const fillPct = hasValue ? pct : (isSetOnly ? (block.token_fill_pct || 0) : 0);
    const valueW = Math.round(140 * s);
    const labelW = plotW - valueW - Math.round(16 * s);

    let valueText;
    if (dv) valueText = dv;
    else if (chartData.value == null) valueText = (chartData.null_fallback && chartData.null_fallback.label) || "N/A";
    else if (typeof chartData.target === "number") valueText = `${pct}% / ${chartData.target}%`;
    else valueText = `${Math.round(pct)}%`;

    const labelNode = new CustomText({
      x: padding.left, y: padding.top, width: labelW,
      text: block.label || "", fontSize: Math.round(12 * s),
      fill: isNA ? "#888" : "#333", lineHeight: 1.2, refreshCallback: imageCallback
    });
    group.add(labelNode);

    const valueNode = new CustomText({
      x: padding.left + labelW + Math.round(8 * s), y: padding.top,
      width: valueW, align: "right",
      text: valueText, fontSize: Math.round(12 * s), fontStyle: "bold",
      fill: isNA ? "#888" : barColor, refreshCallback: imageCallback
    });
    group.add(valueNode);
    if (block.id) {
      tagEvidence(labelNode, `${block.id}.value`, options);
      tagEvidence(valueNode, `${block.id}.value`, options);
    }

    const textH = Math.max(labelNode.height(), valueNode.height(), Math.round(14 * s));
    const gap = Math.round(4 * s);
    const availableForBar = Math.max(2, height - padding.top - padding.bottom - textH - gap);
    const actualBarH = Math.min(barH, availableForBar);
    const barY = padding.top + textH + gap;

    group.add(new Konva.Rect({
      x: padding.left, y: barY, width: plotW, height: actualBarH,
      fill: "#e8e8e8", cornerRadius: Math.round(actualBarH / 2)
    }));
    if (fillPct > 0) {
      group.add(new Konva.Rect({
        x: padding.left, y: barY,
        width: (plotW * fillPct) / 100, height: actualBarH,
        fill: barColor, opacity: isSetOnly ? 0.5 : 1, cornerRadius: Math.round(actualBarH / 2)
      }));
    }
    group.height(padding.top + textH + gap + actualBarH + padding.bottom);
  } else if (block.kind === "line") {
    const dx = xLabels.length > 1 ? plotW / (xLabels.length - 1) : plotW;
    for (const ser of (block.series || [])) {
      const values = chartData.series[ser.key] || [];
      const color = evaluateConditionalColor(ser.color, chartData);
      const points = [];
      for (let vi = 0; vi < values.length; vi++) {
        const v = values[vi];
        if (v == null) continue;
        points.push(padding.left + vi * dx);
        points.push(yToPx(v));
      }
      if (points.length >= 4) {
        const lineAttrs = { points, stroke: color, strokeWidth: Math.round(2 * s), lineJoin: "round", tension: 0.3 };
        if (ser.dash) lineAttrs.dash = ser.dash;
        group.add(new Konva.Line(lineAttrs));
        const dotRadius = Math.round(4 * s);
        for (let vi = 0; vi < values.length; vi++) {
          if (values[vi] == null) continue;
          group.add(new Konva.Circle({
            x: padding.left + vi * dx, y: yToPx(values[vi]),
            radius: dotRadius, fill: color, stroke: "#fff", strokeWidth: Math.round(1.5 * s)
          }));
        }
        if (block.last_point && block.last_point.dot !== false) {
          const lastX = points[points.length - 2];
          const lastY = points[points.length - 1];
          const dotColor = block.last_point.dot_color
            ? evaluateConditionalColor(block.last_point.dot_color, chartData) : color;
          group.add(new Konva.Circle({ x: lastX, y: lastY, radius: Math.round(6 * s), fill: dotColor, stroke: "#fff", strokeWidth: Math.round(2.5 * s) }));
        }
        if (ser.label_position === "above_bar") {
          for (let vi = 0; vi < values.length; vi++) {
            if (values[vi] == null) continue;
            group.add(new CustomText({
              x: padding.left + vi * dx - Math.round(30 * s), y: yToPx(values[vi]) - Math.round(22 * s),
              width: Math.round(60 * s),
              text: formatPointLabel(values[vi], effYAxis, ser, chartData),
              fontSize: Math.round(13 * s), fill: "#333", align: "center", fontStyle: "bold",
              refreshCallback: imageCallback
            }));
          }
        }
      }
    }
  } else {
    // Bar chart — use the same yMin/yMax/yRange as the y-axis so bars
    // align with gridlines. When yMin < 0 shrink barPlotH to leave room
    // for negative-value x-axis labels below the zero line.
    const needsNegativeSpace = yMin < 0;
    const axisLabelH = Math.round(16 * s);
    barPlotH = plotH - (needsNegativeSpace ? axisLabelH : 0);
    barYScale = barPlotH / yRange;
    baselineY = padding.top + (yMax / yRange) * barPlotH;
    const barWidth = (barGroupWidth * 0.55) / Math.max(seriesList.length, 1);
    const barGap = barGroupWidth * 0.05;

    for (let si = 0; si < seriesList.length; si++) {
      const ser = seriesList[si];
      const values = chartData.series[ser.key] || [];
      const partialFlags = (chartData.series_partial || {})[ser.key] || [];
      const partialMonths = (chartData.series_period_months || {})[ser.key] || [];
      const baseColor = evaluateConditionalColor(ser.color, chartData);
      for (let vi = 0; vi < values.length; vi++) {
        const val = values[vi];
        if (val == null) continue;
        const isPartial = partialFlags[vi] === true;
        const absH = Math.abs(val) * barYScale;
        const barH = Math.max(1, absH);
        const barX = padding.left + vi * barGroupWidth + barGroupWidth * 0.2 + si * (barWidth + barGap);
        const barY = val >= 0 ? baselineY - barH : baselineY;
        group.add(new Konva.Rect({
          x: barX, y: barY, width: barWidth, height: barH, fill: baseColor,
          opacity: isPartial ? 0.45 : 1,
          ...(isPartial ? { stroke: baseColor, strokeWidth: Math.round(1 * s), dash: [Math.round(3 * s), Math.round(2 * s)] } : {}),
          cornerRadius: Math.round(1 * s)
        }));
        if (ser.label_position === "above_bar") {
          let formattedVal = formatPointLabel(val, effYAxis, ser, chartData);
          if (isPartial) {
            const months = partialMonths[vi];
            formattedVal += months ? ` · ${months}M` : "*";
          }
          group.add(new CustomText({
            x: barX - Math.round(12 * s), y: val >= 0 ? barY - Math.round(16 * s) : barY + barH + Math.round(4 * s),
            width: barWidth + Math.round(24 * s),
            text: formattedVal, fontSize: Math.round(11 * s),
            fill: isPartial ? "#777" : "#333", align: "center",
            ...(isPartial ? { fontStyle: "italic" } : {}),
            refreshCallback: imageCallback
          }));
        }
      }
    }
  }

  // Partial-period support: which x positions hold at least one sub-annual value
  const partialByIndex = (i) => Object.values(chartData.series_partial || {}).some(flags => flags?.[i] === true);
  const hasAnyPartial = (xLabels || []).some((_, i) => partialByIndex(i));

  // ── X-axis labels ──
  const axisLabelY = padding.top + plotH + Math.round(6 * s);
  const labelFontSize = Math.round(12 * s);
  const labelWidth = Math.round(80 * s);
  const minLabelGap = Math.round(70 * s);
  const perLabelSpan = xLabels.length > 1 ? plotW / (xLabels.length - 1) : plotW;
  const labelStride = perLabelSpan > 0 ? Math.max(1, Math.ceil(minLabelGap / perLabelSpan)) : 1;
  const labelIndices = new Set();
  if (xLabels.length > 0) labelIndices.add(0);
  if (xLabels.length > 1) labelIndices.add(xLabels.length - 1);
  for (let i = labelStride; i < xLabels.length - 1; i += labelStride) {
    if ((xLabels.length - 1 - i) * perLabelSpan < minLabelGap) continue;
    labelIndices.add(i);
  }
  for (let i = 0; i < xLabels.length; i++) {
    if (!labelIndices.has(i)) continue;
    const isPartialSlot = partialByIndex(i);
    group.add(new CustomText({
      x: padding.left + i * barGroupWidth + barGroupWidth / 2 - Math.round(labelWidth / 2),
      y: axisLabelY, width: labelWidth,
      text: isPartialSlot ? `${xLabels[i]}*` : xLabels[i], fontSize: labelFontSize,
      fill: "#666", align: "center", refreshCallback: imageCallback
    }));
  }

  // Footnote explaining the partial-period marking (dashed faded bars, '*').
  if (hasAnyPartial && block.kind !== "pie" && block.kind !== "coverage" && !isProgress) {
    const months = [...new Set(
      Object.values(chartData.series_period_months || {}).flat().filter(m => m != null)
    )].sort((a, b) => a - b);
    const defaultNote = months.length
      ? `* partial period (${months.map(m => `${m}M`).join("/")}) — not full year`
      : "* partial period — not full year";
    group.add(new CustomText({
      x: padding.left, y: axisLabelY + labelFontSize + Math.round(4 * s),
      width: plotW,
      text: block.partial_footnote || defaultNote,
      fontSize: Math.round(9 * s), fill: "#888", fontStyle: "italic",
      refreshCallback: imageCallback
    }));
  }

  // ── Legend ──
  if (seriesList.length > 1) {
    let lx = padding.left;
    for (const ser of seriesList) {
      const color = evaluateConditionalColor(ser.color, chartData);
      group.add(new Konva.Circle({ x: lx + Math.round(5 * s), y: Math.round(11 * s), radius: Math.round(5 * s), fill: color }));
      group.add(new CustomText({
        x: lx + Math.round(14 * s), y: Math.round(4 * s), width: Math.round(100 * s),
        text: ser.label, fontSize: Math.round(11 * s), fill: "#666", refreshCallback: imageCallback
      }));
      lx += Math.round(110 * s);
    }
  }

  addDebugOverlay(group, block, options);
  return group;
}
